import { defineComponent as _defineComponent } from 'vue'
import { withKeys as _withKeys, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers, createBlock as _createBlock, withCtx as _withCtx, createVNode as _createVNode, renderSlot as _renderSlot } from "vue"

const _hoisted_1 = ["type", "name", "value", "placeholder", "autocomplete"]
const _hoisted_2 = {
  key: 0,
  class: "floating-label__required"
}
const _hoisted_3 = {
  key: 2,
  class: "search_buttons_container"
}
const _hoisted_4 = ["src"]
const _hoisted_5 = ["src"]
const _hoisted_6 = ["src"]
const _hoisted_7 = ["src"]

import {
	ref,
	computed,
	onMounted,
	watch,
	onBeforeUnmount
} from 'vue';
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import { debounce } from "lodash";

type AppInputProps = {
	modelValue: string,
	type?: string,
	name?: string,
	label?: string,
	placeholder?: string,
	autocomplete?: string,
	error?: boolean,
	focused?: boolean,
	// Очистка поля
	showClearButton?: boolean,
	clearHintText?: string,
	// Добавление
	showAddButton?: boolean,
	addHintText?: string,
	// Поиск
	showSearchButton?: boolean,
	searchHintText?: string,
	// Валидация
	validationRegex?: RegExp | string | null,
	showValidationIcon?: boolean,
	errorHintText?: string,
	validationHintText?: string,
	// Иконки
	iconCheckPath?: string,
	iconClearName?: string,
	iconInvalidPath?: string,
	iconVisiblePath?: string,
	iconUnvisiblePath?: string,
	// Отображение пароля
	showPasswordToggle?: boolean,
	showPasswordHintText?: string,
	hidePasswordHintText?: string,
	// Дополнительные опции
	disableAutofillDetection?: boolean,
	// Дебаунс эмита значения наверх (мс). 0 = синхронно
	// Локальное отображение символа всегда мгновенное, дебаунсятся только эмиты update:modelValue/input.
	debounce?: number,
	// Обязательное поле — рисует красную звёздочку у floating-label
	required?: boolean,
	// Плейсхолдер рисуется своим элементом и при нехватке ширины обрезается с многоточием.
	// Нативный плейсхолдер Chrome в фокусе обрезает без многоточия, и CSS это не меняет.
	ellipsisPlaceholder?: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AppInput',
  props: {
    modelValue: {},
    type: { default: 'text' },
    name: { default: '' },
    label: { default: '' },
    placeholder: { default: '' },
    autocomplete: { default: 'off' },
    error: { type: Boolean, default: false },
    focused: { type: Boolean, default: false },
    showClearButton: { type: Boolean, default: true },
    clearHintText: { default: '' },
    showAddButton: { type: Boolean, default: false },
    addHintText: { default: '' },
    showSearchButton: { type: Boolean, default: false },
    searchHintText: { default: '' },
    validationRegex: { default: null },
    showValidationIcon: { type: Boolean, default: false },
    errorHintText: { default: '' },
    validationHintText: { default: '' },
    iconCheckPath: { default: '' },
    iconClearName: { default: '' },
    iconInvalidPath: { default: '' },
    iconVisiblePath: { default: '' },
    iconUnvisiblePath: { default: '' },
    showPasswordToggle: { type: Boolean, default: false },
    showPasswordHintText: { default: '' },
    hidePasswordHintText: { default: '' },
    disableAutofillDetection: { type: Boolean, default: false },
    debounce: { default: 0 },
    required: { type: Boolean, default: false },
    ellipsisPlaceholder: { type: Boolean, default: false }
  },
  emits: [
	'update:modelValue',
	'input',
	'focus',
	'blur',
	'clear-error',
	'clear-field',
	'validation-change',
	'password-visibility-change',
	'add',
	'search',
	'pressEnter',
	'pressEsc'
],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props

const emit = __emit

// Пути к иконкам по умолчанию
const defaultIconCheckPath = new URL('@/assets/image/icon-check.svg', import.meta.url).href;
const defaultIconInvalidPath = new URL('@/assets/image/icon-invalid.svg', import.meta.url).href;
const defaultIconVisiblePath = new URL('@/assets/image/icon-visible.svg', import.meta.url).href;
const defaultIconUnvisiblePath = new URL('@/assets/image/icon-unvisible.svg', import.meta.url).href;
const iconAddPath = new URL('@/assets/image/icon-add.svg', import.meta.url).href;
const iconSearchPath = new URL('@/assets/image/icon-search.svg', import.meta.url).href;

// Вычисляемые пути к иконкам
const actualIconCloseName = computed(() =>
	props.iconClearName || 'arrow-left'
);

const actualIconCheckPath = computed(() =>
	props.iconCheckPath || defaultIconCheckPath
);
const actualIconInvalidPath = computed(() =>
	props.iconInvalidPath || defaultIconInvalidPath
);
const actualIconVisiblePath = computed(() =>
	props.iconVisiblePath || defaultIconVisiblePath
);
const actualIconUnvisiblePath = computed(() =>
	props.iconUnvisiblePath || defaultIconUnvisiblePath
);

// Реактивные состояния
const isFocused = ref(props.focused);
const showAddHint = ref(false);
const showSearchHint = ref(false);
const showClearIconMouseHold = ref(false);
const hasValue = ref(false);
const isPasswordVisible = ref(false);
const inputElement = ref<HTMLInputElement | null>(null);
const localValue = ref(props.modelValue ?? '');

// Вычисляемые свойства
const showIcons = computed(() => {
	return props.showSearchButton || isFocused.value || hasValue.value || props.error;
});

const showClearIcon = computed(() => {
	return (isFocused.value || showClearIconMouseHold.value) && localValue.value.length > 0;
});

const compiledRegex = computed<RegExp | null>(() => {
	if (!props.validationRegex) return null;
	return props.validationRegex instanceof RegExp
		? props.validationRegex
		: new RegExp(props.validationRegex);
});

const isValid = computed(() => {
	if (!compiledRegex.value) return true;
	return compiledRegex.value.test(localValue.value);
});

const actualFieldType = computed(() => {
	if (props.type === 'password' && props.showPasswordToggle && isPasswordVisible.value) {
		return 'text';
	}
	return props.type;
});

const visibilityIcon = computed(() => {
	return isPasswordVisible.value ? actualIconUnvisiblePath.value : actualIconVisiblePath.value;
});

const wrapperClasses = computed(() => ({
	'focused': isFocused.value || hasValue.value,
	'has-error': props.error,
	'has-value': hasValue.value,
	'is-valid': isValid.value && !props.error,
	'password-field': props.type === 'password'
}));

const inputStyle = computed(() => {
	let visibleIconsCount = 0;

	// Кнопка очистки
	if (showClearIcon.value && props.showClearButton) visibleIconsCount++;

	// Иконки поиска и добавления
	if (props.showSearchButton) visibleIconsCount++;
	if (props.showAddButton) visibleIconsCount++;

	// Иконки валидации
	if (props.showValidationIcon && isValid.value && !props.error && !isFocused.value) visibleIconsCount++;
	if (props.showValidationIcon && (!isValid.value || props.error) && !isFocused.value) visibleIconsCount++;

	// Кнопка отображения пароля
	if (props.showPasswordToggle && props.type === 'password') visibleIconsCount++;

	const basePadding = 16;
	const iconWidth = 16;
	const iconGap = 4;
	const additionalPadding = visibleIconsCount * (iconWidth + iconGap);

	return {
		paddingRight: `${basePadding + additionalPadding}px`
	};
});

const ellipsisPlaceholderStyle = computed(() => ({
	right: `calc(${inputStyle.value.paddingRight} + 1px)`
}));

// Методы
const checkAutofill = () => {
	if (props.disableAutofillDetection) return;

	if (inputElement.value) {
		const isAutofilled =
			inputElement.value.matches(':-webkit-autofill') ||
			inputElement.value.matches(':autofill') ||
			inputElement.value.value.length > 0;

		hasValue.value = Boolean(isAutofilled || (props.modelValue && props.modelValue.length > 0));
	}
};

const emitValueNow = (value: string) => {
	emit('update:modelValue', value);
	emit('input', value);
	emit('validation-change', isValid.value);
};

let debouncedEmit = debounce(emitValueNow, props.debounce);
watch(() => props.debounce, (wait) => {
	debouncedEmit.cancel();
	debouncedEmit = debounce(emitValueNow, wait);
});

const emitValue = (value: string) => {
	if (props.debounce > 0) {
		debouncedEmit(value);
	} else {
		emitValueNow(value);
	}
};

const flushEmit = () => {
	if (props.debounce > 0) {
		debouncedEmit.flush();
	}
};

const cancelEmit = () => {
	if (props.debounce > 0) {
		debouncedEmit.cancel();
	}
};

const handleInput = (event: Event) => {
	const target = event.target as HTMLInputElement;
	localValue.value = target.value;
	hasValue.value = target.value.length > 0;
	emit('clear-error');
	emitValue(target.value);
};

const onFocus = () => {
	isFocused.value = true;
	emit('focus');
};

const onBlur = () => {
	setTimeout(() => {
		isFocused.value = false;
		emit('blur');
	}, 100);
};

const onPressEnter = () => {
	flushEmit();
	emit('pressEnter');
}

const onPressEsc = (event: KeyboardEvent) => {
	if (localValue.value) {
		event.preventDefault();
		event.stopPropagation();
		cancelEmit();
		localValue.value = '';
		hasValue.value = false;
		emit('pressEsc');
		emit('update:modelValue', '');
		emit('input', '');
		emit('clear-field');
		emit('clear-error');
		inputElement.value?.blur();
		return;
	}

	emit('pressEsc');
}

onBeforeUnmount(() => {
  debouncedEmit.cancel();
});

const clearField = (event: Event) => {
	event.preventDefault();

	cancelEmit();
	localValue.value = '';
	hasValue.value = false;
	emit('update:modelValue', '');
	emit('input', '');
	emit('clear-field');
	emit('clear-error');

	setTimeout(() => {
		if (inputElement.value) {
			isFocused.value = true;
			inputElement.value.focus();
		}
	}, 0);
};

const search = (event: Event) => {
	event.preventDefault();

	flushEmit();
	emit('search');

	setTimeout(() => {
		if (inputElement.value) {
			isFocused.value = true;
			inputElement.value.focus();
		}
	}, 0);
};

const add = (event: Event) => {
	event.preventDefault();

	flushEmit();
	emit('add');

	setTimeout(() => {
		if (inputElement.value) {
			isFocused.value = true;
			inputElement.value.focus();
		}
	}, 0);
};

const togglePasswordVisibility = () => {
	isPasswordVisible.value = !isPasswordVisible.value;
	emit('password-visibility-change', isPasswordVisible.value);
};

const handleMouseEnter = () => {
	showClearIconMouseHold.value = true;
};

const handleMouseLeave = () => {
	showClearIconMouseHold.value = false;
};

const focusInput = () => {
	inputElement.value?.focus()
}

watch(() => props.modelValue, (newValue) => {
	if ((newValue ?? '') !== localValue.value) {
		localValue.value = newValue ?? '';
	}

	hasValue.value = Boolean(newValue && newValue.length > 0);

	if (!newValue && props.error) {
		emit('clear-error');
	}
});

watch(() => props.focused, (newValue) => {
	isFocused.value = newValue;
});

watch(() => props.type, (newType) => {
	if (newType === 'password') {
		isPasswordVisible.value = false;
		// hidePasswordIcons();
	}
});

// Хуки жизненного цикла
onMounted(() => {
	setTimeout(() => {
		checkAutofill();
	}, 100);
});

__expose({ focusInput })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["input-wrapper", wrapperClasses.value]),
    onMouseenter: handleMouseEnter,
    onMouseleave: handleMouseLeave
  }, [
    _createElementVNode("input", {
      ref_key: "inputElement",
      ref: inputElement,
      class: _normalizeClass({
				'style-input': true,
				'style-input--error': props.error,
				'style-input--ellipsis-placeholder': props.ellipsisPlaceholder
			}),
      type: actualFieldType.value,
      name: _ctx.name,
      value: localValue.value,
      onInput: handleInput,
      onFocus: onFocus,
      onBlur: onBlur,
      onKeydown: [
        _withKeys(onPressEnter, ["enter"]),
        _withKeys(onPressEsc, ["esc"])
      ],
      style: _normalizeStyle(inputStyle.value),
      placeholder: _ctx.placeholder,
      autocomplete: _ctx.autocomplete
    }, null, 46, _hoisted_1),
    (_ctx.ellipsisPlaceholder && _ctx.placeholder && !localValue.value && !hasValue.value)
      ? (_openBlock(), _createElementBlock("span", {
          key: 0,
          class: "ellipsis-placeholder",
          style: _normalizeStyle(ellipsisPlaceholderStyle.value)
        }, _toDisplayString(_ctx.placeholder), 5))
      : _createCommentVNode("", true),
    (_ctx.label)
      ? (_openBlock(), _createElementBlock("label", {
          key: 1,
          class: _normalizeClass(["floating-label", { 'active': isFocused.value || hasValue.value }])
        }, [
          _createTextVNode(_toDisplayString(_ctx.label) + " ", 1),
          (_ctx.required)
            ? (_openBlock(), _createElementBlock("span", _hoisted_2, "*"))
            : _createCommentVNode("", true)
        ], 2))
      : _createCommentVNode("", true),
    (showIcons.value)
      ? (_openBlock(), _createElementBlock("span", _hoisted_3, [
          (showClearIcon.value && _ctx.showClearButton)
            ? (_openBlock(), _createBlock(AppTooltip, {
                key: 0,
                text: props.clearHintText || _ctx.$t('clear_field')
              }, {
                activator: _withCtx(() => [
                  (showClearIcon.value && _ctx.showClearButton)
                    ? (_openBlock(), _createBlock(AppIcon, {
                        key: 0,
                        name: actualIconCloseName.value,
                        width: "16px",
                        color: "#979797",
                        cursor: "pointer",
                        onClick: clearField,
                        onMousedown: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["prevent"]))
                      }, null, 8, ["name"]))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }, 8, ["text"]))
            : _createCommentVNode("", true),
          (_ctx.showValidationIcon && isValid.value && !_ctx.error && !isFocused.value)
            ? (_openBlock(), _createBlock(AppTooltip, {
                key: 1,
                text: _ctx.validationHintText || _ctx.$t('ui_field_valid')
              }, {
                activator: _withCtx(() => [
                  _createElementVNode("img", {
                    src: actualIconCheckPath.value,
                    width: "16",
                    height: "16",
                    class: "pointer"
                  }, null, 8, _hoisted_4)
                ]),
                _: 1
              }, 8, ["text"]))
            : _createCommentVNode("", true),
          (_ctx.showValidationIcon && (!isValid.value || _ctx.error) && !isFocused.value && _ctx.errorHintText)
            ? (_openBlock(), _createBlock(AppTooltip, {
                key: 2,
                text: _ctx.errorHintText
              }, {
                activator: _withCtx(() => [
                  _createElementVNode("img", {
                    src: actualIconInvalidPath.value,
                    width: "16",
                    height: "16",
                    class: "pointer"
                  }, null, 8, _hoisted_5)
                ]),
                _: 1
              }, 8, ["text"]))
            : (_ctx.showValidationIcon && (!isValid.value || _ctx.error) && !isFocused.value)
              ? (_openBlock(), _createElementBlock("img", {
                  key: 3,
                  src: actualIconInvalidPath.value,
                  width: "16",
                  height: "16",
                  class: "pointer"
                }, null, 8, _hoisted_6))
              : _createCommentVNode("", true),
          (_ctx.showPasswordToggle && _ctx.type === 'password')
            ? (_openBlock(), _createBlock(AppTooltip, {
                key: 4,
                text: isPasswordVisible.value
					? (_ctx.hidePasswordHintText || _ctx.$t('ui_hide_password'))
					: (_ctx.showPasswordHintText || _ctx.$t('ui_show_password'))
              }, {
                activator: _withCtx(() => [
                  _createElementVNode("img", {
                    src: visibilityIcon.value,
                    width: "16",
                    height: "16",
                    class: "pointer",
                    onClick: togglePasswordVisibility
                  }, null, 8, _hoisted_7)
                ]),
                _: 1
              }, 8, ["text"]))
            : _createCommentVNode("", true),
          (_ctx.showAddButton && localValue.value.length)
            ? (_openBlock(), _createBlock(AppTooltip, {
                key: 5,
                text: _ctx.addHintText || _ctx.$t('ui_add')
              }, {
                activator: _withCtx(() => [
                  _createVNode(AppIcon, {
                    name: "plus",
                    width: "16px",
                    color: "#979797",
                    cursor: "pointer",
                    "hover-color": "#499259",
                    onClick: add,
                    onMousedown: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["prevent"]))
                  })
                ]),
                _: 1
              }, 8, ["text"]))
            : _createCommentVNode("", true),
          (_ctx.showSearchButton)
            ? (_openBlock(), _createBlock(AppTooltip, {
                key: 6,
                text: _ctx.searchHintText || _ctx.$t('search_short')
              }, {
                activator: _withCtx(() => [
                  _createVNode(AppIcon, {
                    name: "search-magnify",
                    width: "16px",
                    color: "#979797",
                    cursor: "pointer",
                    "hover-color": "#499259",
                    onClick: search,
                    onMousedown: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["prevent"]))
                  })
                ]),
                _: 1
              }, 8, ["text"]))
            : _createCommentVNode("", true),
          _renderSlot(_ctx.$slots, "custom-icons")
        ]))
      : _createCommentVNode("", true)
  ], 34))
}
}

})